'use client';

import { useState } from 'react';
import { submitLead } from '@/lib/api';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Chandigarh',
];

interface PopupFormProps {
  onSubmit?: () => void;
  showTitle?: boolean;
}

export function PopupForm({ onSubmit, showTitle = true }: PopupFormProps) {
  const [form, setForm] = useState({
    fullName: '',
    company: '',
    mobile: '',
    email: '',
    state: '',
  });
  const [stateSearch, setStateSearch] = useState('');
  const [stateOpen, setStateOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const filtered = INDIAN_STATES.filter(s =>
    s.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.mobile.match(/^[6-9]\d{9}$/)) e.mobile = 'Enter valid 10-digit mobile number';
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = 'Enter valid email address';
    if (!form.state) e.state = 'Please select your state';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    try {
      await submitLead({
        fullName: form.fullName,
        businessName: form.company || undefined,
        email: form.email,
        phone: form.mobile,
        message: form.state ? `State: ${form.state}` : '',
        source: 'popup-form',
      });
      setSubmitted(true);
      setTimeout(() => {
        onSubmit?.();
      }, 2200);
    } catch (err) {
      console.error('Popup lead submission failed:', err);
      setErrors({ fullName: 'Submission failed. Please try again.' });
    }
  };

  const set = (key: string, val: string) => {
    setForm(p => ({ ...p, [key]: val }));
    if (errors[key]) setErrors(p => ({ ...p, [key]: '' }));
  };

  const inputBase = 'w-full bg-[#F7F9FC] border-[1.5px] border-[#E4E9F2] rounded-[10px] py-[11px] pr-3.5 pl-[42px] text-sm text-[#0D1B3E] outline-none transition-all duration-200 placeholder:text-[#9BAABF] focus:border-[#0050C8] focus:bg-white focus:ring-[3px] focus:ring-[rgba(0,80,200,0.10)] box-border font-sans';
  const inputError = 'border-red-500 focus:ring-[rgba(239,68,68,0.12)]';

  // ─── SUBMITTED STATE ───────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="bg-white rounded-[20px] overflow-hidden flex items-center justify-center min-h-[440px] px-8 py-12 text-center">
        <div>
          <div
            className="w-[72px] h-[72px] rounded-full flex items-center justify-center mx-auto mb-6"
            style={{
              background: 'linear-gradient(135deg, #0050C8 0%, #0078FF 100%)',
              boxShadow: '0 12px 32px rgba(0,80,200,0.28)',
            }}
          >
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M6 16L13 23L26 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h3 className="text-[22px] font-bold text-[#0D1B3E] mb-2.5">
            Request Received!
          </h3>
          <p className="text-[#64748b] text-[15px] max-w-[320px] mx-auto">
            Our expert team at <strong className="text-[#0050C8]">Farsight Solutions LLP</strong> will contact you within <strong>2 business hours</strong>.
          </p>
          <div className="mt-7 inline-flex items-center gap-2 bg-[#f0f7ff] rounded-lg px-[18px] py-2.5">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8S4.41 14.5 8 14.5 14.5 11.59 14.5 8 11.59 1.5 8 1.5zm.75 7.75h-1.5V7h1.5v2.25zm0-3.5h-1.5v-1.5h1.5v1.5z" fill="#0050C8" />
            </svg>
            <span className="text-[13px] text-[#0050C8] font-medium">
              Check your inbox for a confirmation email
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ─── MAIN FORM ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col md:flex-row rounded overflow-hidden shadow-[0_16px_48px_rgba(5,10,35,0.18)] min-h-full font-sans">

      {/* ── LEFT PANEL ─────────────────────────────────────────────────────── */}
     <div className="hidden md:flex md:w-[38%] md:min-w-[220px] md:shrink-0 relative flex-col justify-between overflow-hidden">

  {/* Video Background */}
  <video
    autoPlay
    loop
    muted
    playsInline
    className="absolute inset-0 w-full h-full object-cover"
  >
    <source src="/videos/popup.mp4" type="video/mp4" />
  </video>

  {/* Optional Light Overlay (reduce opacity for clarity) */}
  <div className="absolute inset-0 bg-black/20" />

  {/* Content Wrapper */}
  <div className="relative z-10 flex flex-col justify-between h-full p-[22px_14px]">

    {/* Logo (Top) */}
    <div className="flex justify-center items-center">
      <img
        src="/images/logo.png"
        alt="Farsight Solutions Logo"
        className="w-[40%] object-contain"
      />
    </div>

    {/* Bottom Text */}
    <div>
      <h2
        className="font-extrabold text-[26px] leading-[1.2] tracking-tight"
        style={{ fontFamily: "'Syne', sans-serif", color: "#ffffff" }}
      >
        Build Smarter.<br />
        <span className="text-[#3DB8FF]">Grow Faster.</span>
      </h2>
    </div>

  </div>
</div>

      {/* ── RIGHT PANEL ────────────────────────────────────────────────────── */}
      <div className="flex-1 bg-white p-[22px_14px] flex flex-col justify-center overflow-y-auto">
        {showTitle && (
          <div className="mb-1">
            <span className="inline-block bg-[#EEF3FF] text-[#0050C8] text-[11px] font-bold tracking-[0.1em] uppercase px-3 py-1 rounded-md mb-2.5">
              Free Consultation
            </span>
          </div>
        )}
        <h2 className="font-extrabold text-2xl text-[#0D1B3E] tracking-tight mb-1 leading-[1.2]" style={{ fontFamily: "'Syne', sans-serif" }}>
          Get Started Today
        </h2>
        <p className="text-[#64748b] text-[13.5px] mb-6 leading-relaxed">
          Fill in your details & our experts will reach out within{' '}
          <strong className="text-[#0050C8]">2 business hours</strong>.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          {/* Full Name */}
          <div className="relative mb-4">
            <label className="block text-[13px] font-semibold text-[#2D3A54] mb-1.5 tracking-[0.01em]" htmlFor="fs-fullname">
              Full Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-[13px] top-1/2 -translate-y-1/2 pointer-events-none text-[#7A8FAD]">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M2.5 13.5c0-3.038 2.462-5.5 5.5-5.5s5.5 2.462 5.5 5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </span>
              <input
                id="fs-fullname"
                className={`${inputBase} ${errors.fullName ? inputError : ''}`}
                type="text"
                placeholder="Enter your full name"
                value={form.fullName}
                onChange={e => set('fullName', e.target.value)}
                autoComplete="name"
              />
            </div>
            {errors.fullName && <div className="text-[11.5px] text-red-500 mt-1 flex items-center gap-1">⚠ {errors.fullName}</div>}
          </div>

          {/* Mobile */}
          <div className="relative mb-4">
            <label className="block text-[13px] font-semibold text-[#2D3A54] mb-1.5 tracking-[0.01em]" htmlFor="fs-mobile">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-[13px] top-1/2 -translate-y-1/2 pointer-events-none text-[#7A8FAD]">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="4" y="1" width="8" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                  <circle cx="8" cy="12.5" r="0.8" fill="currentColor" />
                </svg>
              </span>
              <input
                id="fs-mobile"
                className={`${inputBase} ${errors.mobile ? inputError : ''}`}
                type="tel"
                placeholder="10-digit mobile number"
                value={form.mobile}
                onChange={e => set('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                autoComplete="tel"
                inputMode="numeric"
              />
            </div>
            {errors.mobile && <div className="text-[11.5px] text-red-500 mt-1 flex items-center gap-1">⚠ {errors.mobile}</div>}
          </div>

          {/* Email */}
          <div className="relative mb-4">
            <label className="block text-[13px] font-semibold text-[#2D3A54] mb-1.5 tracking-[0.01em]" htmlFor="fs-email">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-[13px] top-1/2 -translate-y-1/2 pointer-events-none text-[#7A8FAD]">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M1.5 5.5L8 9.5l6.5-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </span>
              <input
                id="fs-email"
                className={`${inputBase} ${errors.email ? inputError : ''}`}
                type="email"
                placeholder="Enter your email"
                value={form.email}
                onChange={e => set('email', e.target.value)}
                autoComplete="email"
              />
            </div>
            {errors.email && <div className="text-[11.5px] text-red-500 mt-1 flex items-center gap-1">⚠ {errors.email}</div>}
          </div>

          {/* Select State */}
          <div className="relative mb-5">
            <label className="block text-[13px] font-semibold text-[#2D3A54] mb-1.5 tracking-[0.01em]" htmlFor="fs-state">
              Select State <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-[13px] top-1/2 -translate-y-1/2 pointer-events-none text-[#7A8FAD]">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1.5C5.51 1.5 3.5 3.51 3.5 6c0 3.375 4.5 9 4.5 9s4.5-5.625 4.5-9c0-2.49-2.01-4.5-4.5-4.5zm0 6.12c-1.18 0-2.12-.95-2.12-2.12S6.82 3.38 8 3.38s2.12.95 2.12 2.12S9.18 7.62 8 7.62z" fill="currentColor" />
                </svg>
              </span>
              <div
                id="fs-state"
                className={`${inputBase} cursor-pointer select-none pr-[38px] flex items-center ${errors.state ? inputError : ''}`}
                onClick={() => setStateOpen(p => !p)}
              >
                <span className={`text-sm ${form.state ? 'text-[#0D1B3E]' : 'text-[#9BAABF]'}`}>
                  {form.state || 'Search or select your state'}
                </span>
              </div>
              {/* Chevron */}
              <span
                className="absolute right-[13px] top-1/2 -translate-y-1/2 pointer-events-none text-[#7A8FAD] transition-transform duration-200"
                style={{ transform: `translateY(-50%) rotate(${stateOpen ? '180deg' : '0deg'})` }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>

              {stateOpen && (
                <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white border-[1.5px] border-[#E4E9F2] rounded-xl max-h-[200px] overflow-y-auto z-50 shadow-[0_8px_28px_rgba(0,0,0,0.12)]">
                  <input
                    className="w-full px-3.5 py-2.5 border-none border-b border-[#E4E9F2] outline-none text-[13px] font-sans text-[#0D1B3E] bg-[#F7F9FC] rounded-t-xl box-border"
                    type="text"
                    placeholder="Search state..."
                    value={stateSearch}
                    onChange={e => setStateSearch(e.target.value)}
                    autoFocus
                    onClick={e => e.stopPropagation()}
                  />
                  {filtered.length === 0 ? (
                    <div className="px-4 py-2.5 text-[13.5px] text-[#9BAABF] cursor-default">No results</div>
                  ) : (
                    filtered.map(s => (
                      <div
                        key={s}
                        className={`px-4 py-2.5 text-[13.5px] cursor-pointer transition-colors duration-100 hover:bg-[#EEF3FF] hover:text-[#0050C8] ${form.state === s ? 'bg-[#EEF3FF] text-[#0050C8] font-semibold' : 'text-[#2D3A54]'}`}
                        onClick={() => {
                          set('state', s);
                          setStateOpen(false);
                          setStateSearch('');
                        }}
                      >
                        {s}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {errors.state && <div className="text-[11.5px] text-red-500 mt-1 flex items-center gap-1">⚠ {errors.state}</div>}
          </div>

          <button
            type="submit"
            className="inline-flex items-center px-5 py-2 rounded-md text-white font-medium bg-blue-600 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
          >
            Get Free Consultation →
          </button>
        </form>

        <div className="flex items-center gap-1.5 text-xs text-[#7A8FAD] mt-3.5 justify-center">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M6.5 1L8.4 4.9l4.3.63-3.1 3.02.73 4.27L6.5 10.6l-3.83 2.02.73-4.27L.3 5.53l4.3-.63L6.5 1z" fill="#10b981" />
          </svg>
          Your information is secure &amp; will not be shared
        </div>
      </div>
    </div>
  );
}